<template>
  <UserLayout>
    <div v-if="user || $store.getters.sharePreview" class="seo-detail">
      <v-progress-linear
        v-if="loading"
        color="accent"
        indeterminate
      />

      <v-container
        v-else-if="!hasArticle"
        class="py-12"
        style="max-width: 720px"
      >
        <v-card outlined class="pa-8 text-center seo-empty">
          <v-icon size="56" color="primary" class="mb-4">insights</v-icon>
          <h1 class="text-h5 font-weight-bold mb-2">{{ $t('seoScore.detail.emptyTitle') }}</h1>
          <p class="text--secondary mb-6">{{ $t('seoScore.detail.emptyBody') }}</p>
          <v-btn color="primary" depressed class="text-none" to="/">
            {{ $t('seoScore.detail.openArticles') }}
          </v-btn>
        </v-card>
      </v-container>

      <div v-else class="seo-detail-shell">
        <header class="seo-detail-header">
          <div class="d-flex align-start">
            <div class="seo-score-badge mr-4" :class="scoreTone">
              {{ seoScore }}
            </div>
            <div class="flex-grow-1">
              <div class="d-flex flex-wrap align-center mb-1">
                <h1 class="text-h5 font-weight-bold mr-3 mb-1">
                  {{ $t('seoScore.detail.title') }}
                </h1>
                <v-chip
                  small
                  class="text-none font-weight-bold mb-1"
                  :color="scoreChipColor"
                  text-color="white"
                >
                  {{ scoreHeadline }}
                </v-chip>
              </div>
              <p class="text--secondary mb-0 seo-detail-subtitle">
                {{ $t('seoScore.detail.subtitle') }}
              </p>
            </div>
            <v-btn icon class="ml-2" :aria-label="$t('seoScore.detail.close')" @click="closePage">
              <v-icon>close</v-icon>
            </v-btn>
          </div>

          <div class="d-flex flex-wrap align-center mt-5 seo-filter-row">
            <div class="d-flex flex-wrap">
              <button
                v-for="filter in statusFilters"
                :key="filter.id"
                type="button"
                class="seo-filter-chip"
                :class="['seo-filter-chip--' + filter.id, { 'seo-filter-chip--active': statusFilter === filter.id }]"
                @click="statusFilter = filter.id"
              >
                <v-icon x-small class="mr-1">{{ filter.icon }}</v-icon>
                {{ filter.label }} ({{ filter.count }})
              </button>
            </div>
            <v-spacer />
            <div class="caption text--secondary mt-2 mt-sm-0">
              {{ $t('seoScore.detail.words', [articleWordTotal]) }}
              &nbsp;·&nbsp;
              {{ $t('seoScore.detail.readTime', [articleReadMinutes]) }}
            </div>
          </div>
        </header>

        <div class="seo-detail-body">
          <aside class="seo-detail-nav">
            <div class="caption font-weight-bold text--secondary mb-3 seo-nav-label">
              {{ $t('seoScore.detail.categoriesLabel') }}
            </div>
            <button
              v-for="category in categories"
              :key="category.id"
              type="button"
              class="seo-nav-item"
              :class="{ 'seo-nav-item--active': activeCategory === category.id }"
              @click="activeCategory = category.id"
            >
              <v-icon small class="mr-2">{{ category.icon }}</v-icon>
              <span class="seo-nav-item-label">{{ $t('seoScore.detail.categories.' + category.id) }}</span>
            </button>

            <div class="seo-geo-card mt-6">
              <div class="font-weight-bold mb-2">{{ $t('seoScore.detail.geoTitle') }}</div>
              <p class="mb-0">{{ $t('seoScore.detail.geoBody') }}</p>
            </div>
          </aside>

          <section class="seo-detail-list">
            <article
              v-for="card in visibleCards"
              :key="card.name"
              class="seo-factor"
              :class="'seo-factor--' + card.status.toLowerCase()"
            >
              <div class="d-flex align-start">
                <v-icon class="mr-3 mt-1" :color="statusColor(card.status)">
                  {{ statusIcon(card.status) }}
                </v-icon>
                <div class="flex-grow-1">
                  <div class="d-flex flex-wrap align-center">
                    <h2 class="text-subtitle-1 font-weight-bold mb-0 mr-auto pr-3 seo-factor-heading">
                      <span class="seo-factor-title">{{ $t('seoScore.' + card.name + '.label') }}</span>
                      <span class="seo-factor-lucy-slot">
                        <LucySpark :metric="card.name" />
                      </span>
                    </h2>
                    <v-chip
                      x-small
                      class="text-none font-weight-bold mr-2 mb-1"
                      :color="statusColor(card.status)"
                      text-color="white"
                    >
                      {{ $t('seoScore.detail.status.' + card.status.toLowerCase()) }}
                    </v-chip>
                    <v-btn
                      v-if="card.status === 'GREEN'"
                      x-small
                      depressed
                      class="text-none mb-1"
                      color="success"
                      disabled
                    >
                      <v-icon x-small left>check</v-icon>
                      {{ $t('seoScore.detail.ok') }}
                    </v-btn>
                    <v-btn
                      v-else-if="!$store.getters.sharePreview"
                      x-small
                      depressed
                      class="text-none mb-1"
                      :color="card.status === 'RED' ? 'error' : 'warning'"
                      :loading="improving === card.name"
                      dark
                      @click="improveOne(card.name)"
                    >
                      <v-icon x-small left>flash_on</v-icon>
                      {{ $t('seoScore.detail.improve') }}
                    </v-btn>
                  </div>
                  <p class="mb-0 mt-2 body-2">
                    {{ $t('seoScore.' + card.name + '.description', card.params) }}
                    <template v-if="card.name === 'keywordInSlug' && isAnalyze">
                      {{ $t('article.urlSuggestion.redirectNote') }}
                    </template>
                  </p>
                    <div v-if="card.status !== 'GREEN'" class="seo-factor-tip mt-3">
                    <v-icon x-small color="primary" class="mr-1">lightbulb</v-icon>
                    {{ $te('seoScore.detail.tips.' + card.name) ? $t('seoScore.detail.tips.' + card.name) : $t('seoScore.' + card.name + '.' + card.status.toLowerCase(), card.params) }}
                  </div>
                  <SeoAiVisibilityPanel v-if="card.name === 'aiVisibility'" />
                  <SeoEeatPanel v-if="card.name === 'eeatScore'" />
                </div>
              </div>
            </article>

            <v-card v-if="!visibleCards.length" outlined class="pa-8 text-center">
              <div class="text--secondary">{{ $t('seoScore.detail.noFactors') }}</div>
            </v-card>
          </section>
        </div>

        <footer class="seo-detail-footer">
          <v-btn text class="text-none" :disabled="seoScore < 80" @click="celebrate">
            <v-icon left small>emoji_events</v-icon>
            {{ $t('seoScore.detail.celebrate') }}
          </v-btn>
          <v-spacer />
          <v-btn outlined class="text-none mr-3" @click="closePage">
            {{ $t('seoScore.detail.close') }}
          </v-btn>
          <v-btn
            v-if="!$store.getters.sharePreview"
            color="primary"
            depressed
            class="text-none"
            :disabled="!improvableCount"
            :loading="improving === 'all'"
            @click="improveAll"
          >
            <v-icon left small>flash_on</v-icon>
            {{ $t('seoScore.detail.improveAll', [improvableCount]) }}
          </v-btn>
        </footer>
      </div>
    </div>
  </UserLayout>
</template>

<script lang="ts">
    import Vue from "vue";
    import UserLayout from "@/layouts/UserLayout.vue";
    import UserPlanMixin from "@/mixins/UserPlanMixin";
    import SeoScoreMetricsMixin from "@/mixins/SeoScoreMetricsMixin";
    import SeoAiVisibilityPanel from "@/components/seoscore/SeoAiVisibilityPanel.vue";
    import SeoEeatPanel from "@/components/seoscore/SeoEeatPanel.vue";
    import LucySpark from "@/components/lucy/LucySpark.vue";
    import {
        seoScoreCategories,
        SeoScoreCategoryId,
        SeoScoreMetricName,
        seoScoreMetricNames,
        SeoScoreStatus
    } from "@/model/SeoScore";
    import {canAutoImproveMetric, improveSeoMetric} from "@/service/seoScoreImprove";
    import {Firestore} from "@/plugins/Firestore";
    import {Article} from "@/model/Article";
    import {captureException} from "@/plugins/Sentry";
    import {Toast} from "@/model/Toast";
    import {applySharedArticleToStore, fetchSharedArticle} from "@/service/articleShare";

    type StatusFilter = 'all' | SeoScoreStatus;

    type SeoDetailVm = Vue & {
        getStatus(name: SeoScoreMetricName): SeoScoreStatus;
        getTrParams(name: SeoScoreMetricName): Array<unknown>;
        stats: Record<SeoScoreStatus, number>;
    };

    function asSeoVm(vm: Vue): SeoDetailVm {
        return vm as SeoDetailVm;
    }

    export default Vue.extend({
        name: "SeoScoreDetailView",
        components: {UserLayout, SeoAiVisibilityPanel, SeoEeatPanel, LucySpark},
        mixins: [UserPlanMixin, SeoScoreMetricsMixin],
        data: () => ({
            loading: false,
            statusFilter: 'all' as StatusFilter,
            activeCategory: 'all' as SeoScoreCategoryId,
            improving: '' as string,
            categories: seoScoreCategories,
        }),
        computed: {
            hasArticle(): boolean {
                return !!this.$store.getters.article.id;
            },
            isAnalyze(): boolean {
                return !!this.$store.getters.article.isAnalyze;
            },
            scoreTone(): string {
                if (this.seoScore >= 80) return 'ok';
                if (this.seoScore >= 50) return 'warn';
                return 'bad';
            },
            scoreChipColor(): string {
                if (this.seoScore >= 80) return 'success';
                if (this.seoScore >= 50) return 'warning';
                return 'error';
            },
            scoreHeadline(): string {
                if (this.seoScore >= 80) return String(this.$t('seoScore.detail.lookingGood'));
                if (this.seoScore >= 50) return String(this.$t('seoScore.detail.canImprove'));
                return String(this.$t('seoScore.detail.needsOptimization'));
            },
            statusFilters(): Array<{ id: StatusFilter; icon: string; label: string; count: number }> {
                const stats = asSeoVm(this).stats;
                return [
                    {id: 'all', icon: 'filter_list', label: String(this.$t('seoScore.detail.filterAll')), count: seoScoreMetricNames.length},
                    {id: 'GREEN', icon: 'check_circle', label: String(this.$t('seoScore.detail.filterGreen')), count: stats.GREEN},
                    {id: 'YELLOW', icon: 'warning', label: String(this.$t('seoScore.detail.filterYellow')), count: stats.YELLOW},
                    {id: 'RED', icon: 'cancel', label: String(this.$t('seoScore.detail.filterRed')), count: stats.RED},
                ];
            },
            categoryMetrics(): SeoScoreMetricName[] {
                const category = seoScoreCategories.find(item => item.id === this.activeCategory);
                if (!category || category.metrics === 'all') {
                    return [...seoScoreMetricNames];
                }
                return category.metrics;
            },
            visibleCards(): Array<{ name: SeoScoreMetricName; status: SeoScoreStatus; params: Array<unknown> }> {
                const vm = asSeoVm(this);
                const rank: Record<SeoScoreStatus, number> = {RED: 0, YELLOW: 1, GREEN: 2};
                return this.categoryMetrics
                    .map(name => ({
                        name,
                        status: vm.getStatus(name),
                        params: vm.getTrParams(name),
                    }))
                    .filter(card => this.statusFilter === 'all' || card.status === this.statusFilter)
                    .sort((a, b) => rank[a.status] - rank[b.status]);
            },
            improvableCount(): number {
                const vm = asSeoVm(this);
                return seoScoreMetricNames.filter(name => vm.getStatus(name) !== 'GREEN').length;
            },
        },
        created(): void {
            this.ensureArticle();
        },
        methods: {
            statusColor(status: SeoScoreStatus): string {
                return status === 'GREEN' ? 'success' : status === 'YELLOW' ? 'warning' : 'error';
            },
            statusIcon(status: SeoScoreStatus): string {
                return status === 'GREEN' ? 'check_circle' : status === 'YELLOW' ? 'warning' : 'cancel';
            },
            articleReturnPath(): string {
                const shareToken = this.$store.getters.sharePreviewToken || this.$route.query.share;
                if (shareToken) {
                    return `/s/${shareToken}`;
                }
                const id = this.$store.getters.article.id || this.$route.query.articleId;
                return id ? `/article/${id}` : '/';
            },
            closePage(): void {
                this.$router.push(this.articleReturnPath());
            },
            celebrate(): void {
                this.$store.dispatch('toasts/add', {
                    type: 'success',
                    title: 'seoScore.detail.celebrateToast',
                } as Toast);
            },
            persistArticle(): Promise<void> {
                if (this.$store.getters.sharePreview) {
                    return Promise.resolve();
                }
                const current = this.$store.getters.article as Article;
                if (!current.id) return Promise.resolve();
                return Firestore.saveUserArticle(current)
                    .then(() => {
                        this.$store.dispatch('setArticleLastSavedSnapshotHash');
                    })
                    .catch(error => {
                        captureException(error);
                    });
            },
            openEditorFor(metricName: SeoScoreMetricName): void {
                const articleId = this.$store.getters.article.id;
                this.$router.push({
                    path: `/article/${articleId}`,
                    query: {seoFocus: metricName},
                }).catch(() => undefined);
            },
            improveOne(metricName: SeoScoreMetricName): void {
                if (this.improving) return;
                const result = improveSeoMetric(metricName);
                if (!result.applied) {
                    this.openEditorFor(metricName);
                    this.$store.dispatch('toasts/add', {
                        type: 'info',
                        title: result.action === 'C' ? 'seoScore.detail.lucyHint' : 'seoScore.detail.editorHint',
                    } as Toast);
                    return;
                }
                this.improving = metricName;
                Promise.resolve(this.persistArticle())
                    .then(() => {
                        this.$store.dispatch('toasts/add', {
                            type: 'success',
                            title: 'seoScore.detail.applied',
                        } as Toast);
                    })
                    .finally(() => {
                        this.improving = '';
                    });
            },
            improveAll(): void {
                if (this.improving) return;
                this.improving = 'all';
                let applied = 0;
                const vm = asSeoVm(this);
                seoScoreMetricNames.forEach(name => {
                    if (vm.getStatus(name) === 'GREEN') return;
                    if (!canAutoImproveMetric(name)) return;
                    if (improveSeoMetric(name).applied) applied += 1;
                });
                Promise.resolve(this.persistArticle())
                    .then(() => {
                        this.$store.dispatch('toasts/add', {
                            type: applied ? 'success' : 'info',
                            title: applied ? 'seoScore.detail.appliedAll' : 'seoScore.detail.editorHint',
                        } as Toast);
                    })
                    .finally(() => {
                        this.improving = '';
                    });
            },
            ensureArticle(): void {
                const shareToken = String(this.$route.query.share || this.$store.getters.sharePreviewToken || '');
                if (shareToken && !this.$store.getters.article.id) {
                    this.loading = true;
                    fetchSharedArticle(shareToken)
                        .then((article: Article) => {
                            applySharedArticleToStore(article);
                        })
                        .catch(error => {
                            captureException(error);
                        })
                        .finally(() => {
                            this.loading = false;
                        });
                    return;
                }
                const queryId = String(this.$route.query.articleId || '');
                const storedId = this.$store.getters.article.id;
                if (storedId && (!queryId || queryId === storedId)) {
                    return;
                }
                if (!queryId) {
                    return;
                }
                if (!this.$store.getters.authenticated) {
                    this.loading = true;
                    this.$watch('$store.getters.authenticated', (authenticated: boolean) => {
                        if (authenticated) this.loadArticle(queryId);
                    });
                    return;
                }
                this.loadArticle(queryId);
            },
            loadArticle(articleId: string): void {
                this.loading = true;
                Firestore.getUserArticle(articleId)
                    .then((article: Article | null) => {
                        if (article) {
                            this.$store.dispatch('setArticle', Article.fromObject(article));
                            this.$store.dispatch('setArticleLastSavedSnapshotHash');
                        }
                    })
                    .catch(error => {
                        captureException(error);
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            },
        }
    });
</script>

<style lang="sass" scoped>
.seo-detail
    min-height: calc(100vh - 64px)

.seo-detail-shell
    max-width: 1280px
    margin: 0 auto
    padding: 16px 16px 96px

.seo-detail-header
    background: var(--v-surface-base)
    border-radius: 20px
    padding: 20px 24px 16px
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06)

.seo-score-badge
    min-width: 64px
    height: 64px
    border-radius: 16px
    display: flex
    align-items: center
    justify-content: center
    font-size: 1.75rem
    font-weight: 800
    color: #fff
    background: var(--v-warning-base)

    &.warn
        color: #fff
        background: var(--v-warning-base)

    &.bad
        color: #fff
        background: var(--v-error-base)

    &.ok
        color: #fff
        background: var(--v-accent-base)

.seo-detail-subtitle
    max-width: 720px
    line-height: 1.45

.seo-filter-chip
    border: 0
    border-radius: 999px
    padding: 6px 12px
    margin: 0 8px 8px 0
    font-size: 13px
    font-weight: 600
    cursor: pointer
    background: #eef2ff
    color: #3730a3

    &--GREEN
        background: var(--v-accent-base)
        color: #fff

    &--YELLOW
        background: var(--v-warning-base)
        color: #fff

    &--RED
        background: var(--v-error-base)
        color: #fff

    &--active
        box-shadow: inset 0 0 0 2px currentColor

.seo-detail-body
    display: grid
    grid-template-columns: 260px minmax(0, 1fr)
    gap: 20px
    margin-top: 20px

.seo-nav-label
    letter-spacing: 0.08em

.seo-nav-item
    width: 100%
    display: flex
    align-items: center
    text-align: left
    border: 0
    background: transparent
    border-radius: 12px
    padding: 10px 12px
    margin-bottom: 4px
    cursor: pointer
    color: inherit

    &--active
        background: #eef4ff
        color: #1d4ed8
        font-weight: 600

.seo-nav-item-label
    font-size: 14px

.seo-geo-card
    background: #eef6ff
    border-radius: 16px
    padding: 16px
    font-size: 13px
    line-height: 1.45
    color: #1e3a5f

.seo-factor-heading
    display: flex !important
    align-items: center
    flex-wrap: nowrap

.seo-factor-title
    min-width: 0

.seo-factor-lucy-slot
    display: inline-flex
    align-items: center
    flex-shrink: 0
    margin-left: 12px

.seo-factor
    border-radius: 16px
    padding: 16px 18px
    margin-bottom: 12px
    border: 1px solid transparent

    &--green
        background: rgba(110, 170, 52, 0.12)
        border-color: var(--v-accent-base)

    &--yellow
        background: rgba(230, 126, 34, 0.12)
        border-color: var(--v-warning-base)

    &--red
        background: rgba(234, 84, 85, 0.12)
        border-color: var(--v-error-base)

.seo-factor-tip
    background: rgba(255, 255, 255, 0.72)
    border-radius: 12px
    padding: 10px 12px
    font-size: 13px
    line-height: 1.45

.seo-detail-footer
    position: sticky
    bottom: 0
    display: flex
    align-items: center
    flex-wrap: wrap
    gap: 8px
    margin-top: 16px
    padding: 12px 8px
    background: var(--v-background-base)

.theme--dark
    .seo-detail-header
        box-shadow: none

    .seo-filter-chip
        background: #312e81
        color: #c7d2fe

        &--GREEN
            background: var(--v-accent-base)
            color: #fff

        &--YELLOW
            background: var(--v-warning-base)
            color: #fff

        &--RED
            background: var(--v-error-base)
            color: #fff

    .seo-nav-item--active
        background: #1e3a5f
        color: #93c5fd

    .seo-geo-card
        background: #1e3a5f
        color: #dbeafe

    .seo-factor--green
        background: rgba(110, 170, 52, 0.18)
        border-color: var(--v-accent-base)

    .seo-factor--yellow
        background: rgba(230, 126, 34, 0.18)
        border-color: var(--v-warning-base)

    .seo-factor--red
        background: rgba(234, 84, 85, 0.18)
        border-color: var(--v-error-base)

    .seo-factor-tip
        background: rgba(0, 0, 0, 0.2)

@media (max-width: 959px)
    .seo-detail-body
        grid-template-columns: 1fr

    .seo-detail-nav
        display: flex
        flex-wrap: wrap
        gap: 4px

    .seo-nav-item
        width: auto
        margin-bottom: 0

    .seo-geo-card
        width: 100%
        margin-top: 8px !important
</style>
